<div class="lmdb-cache-container">
  <h2>LMDB Cache Admin</h2>
  <div class="lmdb-header">
    <div class="server-selector">
      <div>
        <div class="title">Select the Server:</div>
        <select
          [value]="selectedServer()?.id ?? ''"
          (change)="onServerChange($event)"
        >
          <option value="">-- Select server --</option>
          @for (s of servers; track s.id) {
            <option [value]="s.id">{{ s.label }} ({{ s.apiBaseUrl }})</option>
          }
        </select>
        @if (selectedServer()) {
          <div class="server-badge">Viewing: {{ selectedServer()!.label }}</div>
        }
      </div>
    </div>
    <button class="refresh-stats" type="button" (click)="refreshStores()" [disabled]="loading()">Refresh</button>
  </div>

  @if (error(); as err) {
    <div class="error-banner">{{ err }}</div>
  }

  @if (loading() && !hasStores()) {
    <p>Loading…</p>
  }

  @if (selectedServer() && !loading() && !hasStores() && !error()) {
    <p>No stores configured or server unreachable.</p>
  }

  @if (hasStores()) {
    <div class="lmdb-layout">
      <section class="stores-panel">
        <h3>Stores ({{ stores().length }} total)</h3>
        <ul class="store-list">
          @for (store of storesDisplayRows(); track store.storeId) {
            <li>
              <button
                type="button"
                [class.active]="selectedStoreId() === store.storeId"
                (click)="selectStore(store.storeId)"
              >
                <span class="store-label">{{ store.label }}</span>
                <span class="store-id">{{ store.storeId }}</span>
                @if (store.description) {
                  <span class="store-description">{{ store.description }}</span>
                }
                @if (store.totalKeyCountFormatted !== undefined) {
                  <span class="store-key-count">Total Keys: {{ store.totalKeyCountFormatted }}</span>
                }
                @if (!store.healthy) {
                  <span class="badge unhealthy">Unavailable</span>
                }
              </button>
            </li>
          }
        </ul>
      </section>

      @if (selectedStoreId(); as storeId) {
        @if (storeId === 'ari') {
          <div class="ari-and-databases-column">
            <section class="ari-shard-panel">
              <h3>ARI node (LMDB shard)</h3>
              <p class="ari-shard-hint">
                Node paths come from <code>ARI_LMDB_SHARD_PATHS_JSON</code> on the <strong>lmdb-cache-admin</strong>
                process (same variable as ari-processing). API calls include <code>ariLmdbRootPath</code> so the admin
                opens that shard (same as persistence).
              </p>
              @if (!ariShardsLoaded()) {
                <p class="ari-shard-loading">Loading shard list from server…</p>
              } @else if (ariShardPickerOptions().length === 0) {
                <p class="ari-shard-hint">
                  No shard map in the admin process env — set the same JSON as processing on lmdb-cache-admin, or use
                  <code>LMDB_ARI_PATH</code> only (databases below use the default root).
                </p>
              } @else {
                <label class="ari-shard-label" for="ari-shard-select">Select node</label>
                <select
                  id="ari-shard-select"
                  class="ari-shard-select"
                  [value]="selectedAriShardPath() ?? ''"
                  (change)="onAriNodeChange($event)"
                >
                  <option value="">-- Select node --</option>
                  @for (opt of ariShardSelectRows(); track opt.path) {
                    <option [value]="opt.path">
                      {{ opt.label }} — {{ opt.totalKeyCountFormatted }} keys
                      @if (!opt.healthy) {
                        (unavailable)
                      }
                      — {{ opt.path }}
                    </option>
                  }
                </select>
              }
              @if (selectedAriShardPath(); as arp) {
                <p class="ari-shard-path"><strong>Viewing shard:</strong> {{ arp }}</p>
                <p class="ari-node-key-total"><strong>Node total keys:</strong> {{ ariNodeTotalKeysFormatted() }}</p>
              }
            </section>

            @if (ariDatabasesPanelEnabled()) {
              <section class="databases-panel">
                <h3>Databases</h3>
                @if (storeStats(); as stats) {
                  <p class="stats-summary">Total DBs: <b>{{ stats.databaseCount }}</b></p>
                }
                <ul class="db-list">
                  @for (db of databasesDisplayRows(); track db.name) {
                    <li class="db-row">
                      <button
                        type="button"
                        class="db-select"
                        [class.active]="selectedDbName() === db.name"
                        (click)="selectDb(db.name)"
                      >
                        <span class="db-name">{{ db.name }}</span>
                        @if (db.description) {
                          <span class="db-description">{{ db.description }}</span>
                        }
                        @if (db.keyCountFormatted !== undefined) {
                          <span class="db-key-count">Keys: {{ db.keyCountFormatted }}</span>
                        }
                      </button>
                      @if (db.cacheConfig; as cacheConfig) {
                        @if ((LMDB_ADMIN_PERMISSIONS.START_SEO_CACHE_CONFIG | permissions) | async) {
                          <button
                            type="button"
                            class="start-cache-btn"
                            [disabled]="startingCache()"
                            (click)="confirmStartWebsiteCache(cacheConfig, $event)"
                            [title]="'Start SEO URL cache: ' + db.cacheConfigLabel"
                          >
                            Start Cache
                          </button>
                        }
                      }
                    </li>
                  }
                </ul>
              </section>
            }
          </div>
        } @else {
          @if (ariDatabasesPanelEnabled()) {
            <section class="databases-panel">
              <h3>Databases</h3>
              @if (storeStats(); as stats) {
                <p class="stats-summary">Total DBs: <b>{{ stats.databaseCount }}</b></p>
              }
              <ul class="db-list">
                @for (db of databasesDisplayRows(); track db.name) {
                  <li class="db-row">
                    <button
                      type="button"
                      class="db-select"
                      [class.active]="selectedDbName() === db.name"
                      (click)="selectDb(db.name)"
                    >
                      <span class="db-name">{{ db.name }}</span>
                      @if (db.description) {
                        <span class="db-description">{{ db.description }}</span>
                      }
                      @if (db.keyCountFormatted !== undefined) {
                        <span class="db-key-count">Keys: {{ db.keyCountFormatted }}</span>
                      }
                    </button>
                    @if (db.cacheConfig; as cacheConfig) {
                      @if ((LMDB_ADMIN_PERMISSIONS.START_SEO_CACHE_CONFIG | permissions) | async) {
                        <button
                          type="button"
                          class="start-cache-btn"
                          [disabled]="startingCache()"
                          (click)="confirmStartWebsiteCache(cacheConfig, $event)"
                          [title]="'Start SEO URL cache: ' + db.cacheConfigLabel"
                        >
                          Start Cache
                        </button>
                      }
                    }
                  </li>
                }
              </ul>
            </section>
          }
        }
      }

      @if (selectedStoreId() && selectedDbName() && ariDatabasesPanelEnabled()) {
        <section class="keys-panel">
          <div class="keys-header">
            <h3>Total Keys ({{ selectedDbKeyCountFormatted() }})</h3>
            @if ((LMDB_ADMIN_PERMISSIONS.DELETE_LMDB_ADMIN_ALL | permissions) | async) {
              <button type="button" class="delete-all-keys" (click)="confirmDeleteAllKeys($event)">Delete All
              </button>
            }
          </div>
          <div class="keys-toolbar">
            <input
              class="key-prefix-input"
              type="text"
              placeholder="Filter by prefix"
              [value]="keyPrefix()"
              (input)="onPrefixChange($any($event.target).value)"
            />
          </div>
          <ul class="key-list">
            @for (key of keysList(); track key) {
              <li class="key-item">
                <button
                  type="button"
                  [class.active]="selectedKey() === key"
                  (click)="selectKey(key)"
                >
                  {{ key }}
                </button>
                @if ((LMDB_ADMIN_PERMISSIONS.DELETE_LMDB_ADMIN_KEY | permissions) | async) {
                  <button
                    type="button"
                    class="key-delete"
                    (click)="confirmDeleteKey(key, $event)"
                    title="Delete key"
                    aria-label="Delete key"
                  >&#128465;
                  </button>
                }
              </li>
            }
          </ul>
          @if ((keysResult()?.total ?? 0) > keysList().length) {
            <button type="button" class="load-more" (click)="loadMoreKeys()">Load more</button>
          }
        </section>
      }

      <section class="value-panel" [class.has-selection]="selectedKey()">
        @if (selectedKey(); as key) {
          <div class="value-header">
            <h3>Value (key: {{ key }})</h3>
            @if (selectedValue() !== null && selectedValue() !== undefined) {
              <button
                type="button"
                class="copy-value-btn"
                (click)="copySelectedValue()"
                title="Copy value to clipboard"
                aria-label="Copy value to clipboard"
              >Copy
              </button>
            }
          </div>
          @if (selectedValue() === null || selectedValue() === undefined) {
            <p class="value-empty">No value found or value is null.</p>
          } @else {
            <pre class="value-json">{{ selectedValueDisplay() }}</pre>
          }
        } @else {
          <p class="value-placeholder">Select a key to view its value.</p>
        }
      </section>
    </div>

    <section class="statistics-section">
      <div class="statistics-header">
        <div>
          <h3 class="statistics-title">Cache Statistics Overview</h3>
          <p class="statistics-subtitle">Live overview of all LMDB stores, databases, key counts and cache sizes.</p>
        </div>
        <button type="button" class="refresh-stats" (click)="refreshStatistics()">Refresh</button>
      </div>
      @if (statisticsSummaryVm(); as sum) {
        <div class="statistics-summary">
          <div class="stat-card">
            <span class="stat-value">{{ sum.totalStoresFormatted }}</span>
            <span class="stat-label">Total Stores</span>
          </div>
          <div class="stat-card">
            <span class="stat-value">{{ sum.totalDatabasesFormatted }}</span>
            <span class="stat-label">Total Databases</span>
          </div>
          <div class="stat-card">
            <span class="stat-value">{{ sum.totalKeysFormatted }}</span>
            <span class="stat-label">Total Keys</span>
          </div>
          <div class="stat-card">
            <span class="stat-value">{{ sum.totalSizeFormatted }}</span>
            <span class="stat-label">Total Cache Size</span>
          </div>
        </div>
        <div class="statistics-stores-grid">
          @for (card of statisticsStoreCardsVm(); track card.storeId) {
            <div class="store-stat-card" [class.store-healthy]="card.isHealthy"
                 [class.store-warning]="card.isWarning"
                 [class.store-critical]="card.isCritical">
              <div class="store-stat-header-wrapper">
                <div class="store-stat-header">
                  <strong>{{ card.storeName }} ({{ card.storeId }})</strong>
                  <span class="store-stat-badge">{{ card.totalDatabases }}
                    databases &bull; {{ card.badgeKeysFormatted }}
                    keys &bull; {{ card.badgeSizeFormatted }}</span>
                </div>
                <div class="store-status" [class.healthy]="card.isHealthy"
                     [class.warning]="card.isWarning"
                     [class.critical]="card.isCritical">
                  {{ card.statusLabel }}
                </div>
              </div>

              <table class="store-db-table">
                <thead>
                <tr>
                  <th>Database</th>
                  <th>Keys</th>
                  <th>Size</th>
                </tr>
                </thead>
                <tbody>
                  @for (db of card.databases; track db.name) {
                    <tr>
                      <td>{{ db.name }}</td>
                      <td>{{ db.keyCountFormatted }}</td>
                      <td>{{ db.sizeFormatted }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
        @if (statisticsAriShardCardsVm().length) {
          <h4 class="statistics-ari-shards-title">ARI — per-node (LMDB shard)</h4>
          <p class="statistics-ari-shards-subtitle">Same data as aggregated ARI row above, split by
            <code>ARI_LMDB_SHARD_PATHS_JSON</code> on lmdb-cache-admin.</p>
          <div class="statistics-stores-grid">
            @for (shard of statisticsAriShardCardsVm(); track shard.trackId) {
              <div class="store-stat-card" [class.store-healthy]="shard.isHealthy"
                   [class.store-warning]="shard.isWarning"
                   [class.store-critical]="shard.isCritical">
                <div class="store-stat-header-wrapper">
                  <div class="store-stat-header">
                    <strong>{{ shard.storeName }}</strong>
                    @if (shard.shardPath) {
                      <span class="ari-shard-stat-path">{{ shard.shardPath }}</span>
                    }
                    <span class="store-stat-badge">{{ shard.totalDatabases }}
                      databases &bull; {{ shard.badgeKeysFormatted }}
                      keys &bull; {{ shard.badgeSizeFormatted }}</span>
                  </div>
                  <div class="store-status" [class.healthy]="shard.isHealthy"
                       [class.warning]="shard.isWarning"
                       [class.critical]="shard.isCritical">
                    {{ shard.statusLabel }}
                  </div>
                </div>
                <table class="store-db-table">
                  <thead>
                  <tr>
                    <th>Database</th>
                    <th>Keys</th>
                    <th>Size</th>
                  </tr>
                  </thead>
                  <tbody>
                    @for (db of shard.databases; track db.name) {
                      <tr>
                        <td>{{ db.name }}</td>
                        <td>{{ db.keyCountFormatted }}</td>
                        <td>{{ db.sizeFormatted }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>
        }
      } @else {
        <p class="statistics-loading">Select a server and load statistics.</p>
      }
    </section>
  }
</div>
